import { useCallback, useEffect, useMemo, useState } from 'react'

import useRouter from '../../../hooks/useRouter'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import {
  useFormFn,
  useFormInstanceFn,
  useWatchFn,
} from '../../../shared/antd/ANTDForm'
import { modifyFileListKeys } from '../../../utils/customFunctions'
import {
  entries,
  include,
  isArray,
  isEqual,
  keys,
  length,
} from '../../../utils/javascript'
import { deleteBeneficiaryApi, getJobDetailApi } from '../jobs.api'
import { booleanOptions, payloadType } from '../jobs.description'

const childMotherTable = ({
  userType,
  filePath = [],
  hideActionColumn = false,
  tableData,
  apiCall,
}) => {
  const { t } = useTranslations()
  const inspectionForm = useFormInstanceFn()
  const { params } = useRouter()
  const childrenRequestData = inspectionForm.getFieldValue([
    'inspectionList',
    0,
    'childrenRequestDto',
  ])
  const enrolledNumberKeys = {
    job_Children: 'numberOfChildrenEnrolledAwc',
    job_PregnantWomen: 'numberOfPregnantWomenEnrolledAwc',
    job_LactatingWomen: 'numberOfLactatingWomenEnrolledAwc',
  }
  const enrolledNumber = childrenRequestData?.[enrolledNumberKeys?.[userType]]
  const keyName = filePath?.[filePath.length - 1]

  const getInitialFormData = data => {
    const updatedData =
      data && data?.length && isArray(data)
        ? data?.map((item, index) => {
            const formAttributes = childMotherFormAttrFn({
              photoOfBeneficiaryUploadedValue:
                item?.photoOfBeneficiaryUploadedYesNo,
              takeHomeRationTHRValue: item?.takeHomeRation,
              severeAcuteMalnutritionSAMValue: item?.sam,
              moderateAcuteMalnutritionMAMValue: item?.mam,
            })
            const newItem = {}
            entries(formAttributes)?.forEach(([key, value]) => {
              if (include(keys(formAttributes), key)) {
                if (
                  (key.endsWith('Ids') || key.endsWith('Id')) &&
                  isEqual(value?.inputType, 'formUpload')
                ) {
                  const keyLength = key.endsWith('Ids') ? -3 : -2
                  const detailsKey = `${key.slice(0, keyLength)}Details`

                  newItem[key] = {
                    fileList: length(item?.[detailsKey])
                      ? modifyFileListKeys(item?.[detailsKey])
                      : [],
                  }
                } else {
                  newItem[key] = item?.[key]
                }
              }
            })
            return { ...newItem, sNo: index + 1, id: item?.id }
          })
        : []
    return updatedData || []
  }

  const childMotherFormAttrFn = useCallback(
    ({
      photoOfBeneficiaryUploadedValue,
      takeHomeRationTHRValue,
      severeAcuteMalnutritionSAMValue,
      moderateAcuteMalnutritionMAMValue,
    } = {}) => ({
      imageDmsId: {
        label: 'user_Image',
        inputType: 'formUpload',
        acceptFileTypes: '.png,.jpg,.jpeg',
        uploadSingle: true,
        disableGalleryUpload: true,
        required: true,
        md: 24,
        xs: 24,
      },
      name: {
        label: 'user_Name',
        inputType: 'input',
        required: true,
        md: 24,
        xs: 24,
      },
      guardianDetails: {
        label: isEqual(userType, 'job_Children')
          ? 'job_GuardianNameAndDetails'
          : 'job_GuardianSpouseNameAndDetails',
        inputType: 'input',
        required: true,
        md: 24,
        xs: 24,
      },
      photoOfBeneficiaryUploadedYesNo: {
        label: 'job_PhotoOfBeneficiaryUploaded',
        inputType: 'select',
        responsiveInputType: 'RADIO_BUTTON',
        options: booleanOptions,
        required: true,
        md: 24,
        xs: 24,
      },
      ...(photoOfBeneficiaryUploadedValue === 'YES' && {
        poshanTrackerId: {
          label: 'job_POSHANTrackerId',
          inputType: 'input',
          required: true,
          md: 12,
          xs: 24,
          maxLength: 20,
        },
        beneficiaryDmsId: {
          label: 'user_Image',
          inputType: 'formUpload',
          acceptFileTypes: '.png,.jpg,.jpeg',
          uploadSingle: true,
          disableGalleryUpload: true,
          required: true,
          md: 12,
          xs: 24,
        },
      }),
      takeHomeRation: {
        label: 'job_TakeHomeRationTHR',
        inputType: 'select',
        responsiveInputType: 'RADIO_BUTTON',
        options: booleanOptions,
        required: true,
        md: 24,
        xs: 24,
      },
      ...(takeHomeRationTHRValue === 'YES' && {
        takeHomeRationDmsIds: {
          label: 'job_TakeHomeRationPhotos',
          inputType: 'formUpload',
          acceptFileTypes: '.png,.jpg,.jpeg',
          disableGalleryUpload: true,
          required: true,
          max: 5,
          md: 24,
          xs: 24,
        },
      }),
      ...(takeHomeRationTHRValue === 'NO' && {
        takeHomeRationReason: {
          label: 'job_PleaseSpecifyTheReason',
          inputType: 'textArea',
          required: true,
          md: 24,
          xs: 24,
        },
      }),
      ...(userType === 'job_Children' && {
        sam: {
          label: 'job_SevereAcuteMalnutritionSAM',
          inputType: 'select',
          responsiveInputType: 'RADIO_BUTTON',
          options: booleanOptions,
          required: true,
          md: 24,
          xs: 24,
        },
        ...(severeAcuteMalnutritionSAMValue === 'YES' && {
          samReason: {
            label: 'job_PleaseSpecifyTheReason',
            inputType: 'textArea',
            required: true,
            md: 24,
            xs: 24,
          },
          samDmsIds: {
            label: 'job_SevereAcuteMalnutritionPhotos',
            inputType: 'formUpload',
            acceptFileTypes: '.png,.jpg,.jpeg',
            uploadSingle: false,
            disableGalleryUpload: true,
            required: true,
            max: 5,
            md: 24,
            xs: 24,
          },
        }),
        mam: {
          label: 'job_ModerateAcuteMalnutritionMAM',
          inputType: 'select',
          responsiveInputType: 'RADIO_BUTTON',
          options: booleanOptions,
          required: true,
          md: 24,
          xs: 24,
        },
        ...(moderateAcuteMalnutritionMAMValue === 'YES' && {
          mamReason: {
            label: 'job_PleaseSpecifyTheReason',
            inputType: 'textArea',
            required: true,
            md: 24,
            xs: 24,
          },
          mamDmsIds: {
            label: 'job_ModerateAcuteMalnutritionPhotos',
            inputType: 'formUpload',
            acceptFileTypes: '.png,.jpg,.jpeg',
            uploadSingle: false,
            disableGalleryUpload: true,
            required: true,
            max: 5,
            md: 24,
            xs: 24,
          },
        }),
      }),
    }),
    [],
  )

  const initialFormData = inspectionForm.getFieldValue(filePath) || []
  const rowsInitialData = getInitialFormData(
    hideActionColumn ? tableData : initialFormData,
  )
  const [rows, setRows] = useState(rowsInitialData)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewingRecord, setViewingRecord] = useState(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    record: null,
    index: null,
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: length(rowsInitialData) || 0,
    responsive: true,
  })
  const modalForm = useFormFn()

  const photoOfBeneficiaryUploadedValue = useWatchFn(
    'photoOfBeneficiaryUploadedYesNo',
    modalForm,
  )
  const takeHomeRationTHRValue = useWatchFn('takeHomeRation', modalForm)
  const severeAcuteMalnutritionSAMValue = useWatchFn('sam', modalForm)
  const moderateAcuteMalnutritionMAMValue = useWatchFn('mam', modalForm)

  const columnKeys = {
    sNo: 'sNo',
    imageDmsId: 'imageDmsId',
    name: 'name',
    guardianDetails: 'guardianDetails',
    photoOfBeneficiaryUploaded: 'photoOfBeneficiaryUploaded',
    takeHomeRation: 'takeHomeRation',
    sam: 'sam',
    mam: 'mam',
    action: 'action',
  }

  const {
    sNo,
    imageDmsId,
    name,
    guardianDetails,
    photoOfBeneficiaryUploaded,
    takeHomeRation,
    sam,
    mam,
    action,
  } = columnKeys

  const columns = (onEdit, onView, onDelete) =>
    [
      {
        title: t('txt_SNo'),
        key: sNo,
        dataIndex: 'sNo',
      },
      {
        title: t('user_Image'),
        key: imageDmsId,
        dataIndex: 'imageDmsId',
        render: rowData => {
          const fileData = rowData?.fileList?.[0]
          const fileUrl =
            fileData?.thumbUrl || fileData?.url || fileData?.fileUrl
          return fileUrl ? (
            <img
              src={fileUrl}
              alt="child"
              style={{
                height: 60,
                width: 60,
              }}
            />
          ) : (
            '-'
          )
        },
      },
      {
        title: t('user_Name'),
        key: name,
        dataIndex: 'name',
      },
      {
        title: t(
          isEqual(userType, 'job_Children')
            ? 'job_GuardianNameAndDetails'
            : 'job_GuardianSpouseNameAndDetails',
        ),
        key: guardianDetails,
        dataIndex: 'guardianDetails',
      },
      {
        title: t('job_PhotoOfBeneficiaryUploaded'),
        key: photoOfBeneficiaryUploaded,
        dataIndex: 'photoOfBeneficiaryUploadedYesNo',
      },
      {
        title: t('job_TakeHomeRationTHR'),
        key: takeHomeRation,
        dataIndex: 'takeHomeRation',
      },
      {
        title: t('job_SevereAcuteMalnutritionSAM'),
        key: sam,
        dataIndex: 'sam',
      },
      {
        title: t('job_ModerateAcuteMalnutritionMAM'),
        key: mam,
        dataIndex: 'mam',
      },
      {
        title: t('txt_Action'),
        key: action,
        fixed: 'right',
        render: (_, record, index) => (
          <div className="d-flex gap-10">
            <ANTDButton
              type="primary"
              className="bg-view"
              onClick={() => onView && onView(record, index)}
            >
              {t('btn_View')}
            </ANTDButton>
            {!hideActionColumn && (
              <>
                <ANTDButton
                  type="primary"
                  className="bg-edit"
                  onClick={() => onEdit && onEdit(record, index)}
                >
                  {t('btn_Edit')}
                </ANTDButton>
                <ANTDButton
                  type="primary"
                  className="bg-danger"
                  onClick={() => onDelete && onDelete(record, index)}
                >
                  {t('btn_Delete')}
                </ANTDButton>
              </>
            )}
          </div>
        ),
      },
    ].filter(item => !item.hidden)

  const columnsList = () => {
    switch (userType) {
      case 'job_Children':
        return [
          sNo,
          imageDmsId,
          name,
          guardianDetails,
          photoOfBeneficiaryUploaded,
          takeHomeRation,
          sam,
          mam,
          ...(hideActionColumn ? [] : [action]),
        ]
      case 'job_PregnantWomen':
      case 'job_LactatingWomen':
        return [
          sNo,
          imageDmsId,
          name,
          guardianDetails,
          photoOfBeneficiaryUploaded,
          takeHomeRation,
          ...(hideActionColumn ? [] : [action]),
        ]
      default:
        return []
    }
  }

  const tableColumns = ({ onEdit, onView, onDelete } = {}) => {
    const columnKeys = columnsList() || []
    const allColumns = columns(onEdit, onView, onDelete)
    const otherColumns = columnKeys.map(col => {
      const findColumn = allColumns.find(c => c.key === col)
      return findColumn
    })
    return otherColumns
  }

  const cardViewFn = useCallback(
    record => {
      const guardianLabel = isEqual(userType, 'job_Children')
        ? 'job_GuardianNameAndDetails'
        : 'job_GuardianSpouseNameAndDetails'

      const baseFields = [
        {
          label: 'user_Name',
          value: record?.name,
        },
        {
          label: guardianLabel,
          value: record?.guardianDetails,
        },
        {
          label: 'job_PhotoOfBeneficiaryUploaded',
          value: record?.photoOfBeneficiaryUploadedYesNo,
        },
        {
          label: 'job_TakeHomeRationTHR',
          value: record?.takeHomeRation,
        },
      ]

      if (userType === 'job_Children') {
        return [
          ...baseFields,
          {
            label: 'job_SevereAcuteMalnutritionSAM',
            value: record?.sam,
          },
          {
            label: 'job_ModerateAcuteMalnutritionMAM',
            value: record?.mam,
          },
        ]
      }

      return baseFields
    },
    [userType],
  )

  const formFieldAttributes = useMemo(
    () =>
      childMotherFormAttrFn({
        photoOfBeneficiaryUploadedValue,
        takeHomeRationTHRValue,
        severeAcuteMalnutritionSAMValue,
        moderateAcuteMalnutritionMAMValue,
      }),
    [
      childMotherFormAttrFn,
      photoOfBeneficiaryUploadedValue,
      takeHomeRationTHRValue,
      severeAcuteMalnutritionSAMValue,
      moderateAcuteMalnutritionMAMValue,
    ],
  )

  // Sync rows to form whenever rows change (but not on initial mount to avoid overwriting form data)
  useEffect(() => {
    if (length(filePath) && length(formFieldAttributes)) {
      // Transform rows: convert image fields to dmsId arrays
      const transformedRows = rows.map(row => {
        const transformedRow = { ...row }
        entries(formFieldAttributes)?.forEach(([key, attr]) => {
          if (isEqual(attr?.inputType, 'formUpload') && row?.[key]) {
            // Extract dmsId array from fileList
            const fileList = row[key]?.fileList || []
            const dmsIds = fileList
              ?.map(file => file?.dmsId)
              ?.filter(dmsId => dmsId !== null && dmsId !== undefined)
            transformedRow[key] = length(dmsIds) ? dmsIds : []
          }
        })
        return transformedRow
      })
      inspectionForm.setFieldValue(filePath, transformedRows)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, formFieldAttributes])

  const isAddDisabled = useMemo(() => {
    const totalEnrolled = Number(enrolledNumber ?? 0)
    if (totalEnrolled === 0) {
      return true
    }
    return rows.length >= totalEnrolled
  }, [enrolledNumber, rows.length])

  const handleAddClick = () => {
    setEditingIndex(null)
    modalForm.resetFields()
    setIsModalOpen(true)
  }

  const handleEditClick = (record, index) => {
    setEditingIndex(index)
    modalForm.resetFields()
    modalForm.setFieldsValue(record)
    setIsModalOpen(true)
  }

  const handleViewClick = (record, index) => {
    setViewingRecord(record)
    setIsViewModalOpen(true)
  }

  const handleViewModalCancel = () => {
    setIsViewModalOpen(false)
    setViewingRecord(null)
  }

  const handleDeleteClick = (record, index) => {
    setDeleteConfirmation({
      open: true,
      record,
      index,
    })
  }

  const setFormData = updatedData => {
    const currentFormAttrs = formFieldAttributes
    const transformedRows = updatedData.map(row => {
      const transformedRow = { ...row }
      entries(currentFormAttrs)?.forEach(([key, attr]) => {
        if (isEqual(attr?.inputType, 'formUpload') && row?.[key]) {
          // Extract dmsId array from fileList
          const fileList = row[key]?.fileList || []
          const dmsIds = fileList
            ?.map(file => file?.dmsId)
            ?.filter(dmsId => dmsId !== null && dmsId !== undefined)
          transformedRow[key] = length(dmsIds) ? dmsIds : []
        }
      })
      return transformedRow
    })
    if (length(filePath)) {
      inspectionForm.setFieldValue(filePath, transformedRows)
    }
  }

  const handleDeleteConfirm = async () => {
    const { index, record } = deleteConfirmation
    if (index === null || index === undefined) return
    if (record?.id) {
      const deleteResp = await deleteBeneficiaryApi({
        params: {
          id: record?.id,
          type: 'BENEFICIARY',
          jobType: payloadType?.inspection,
        },
      })
      if (deleteResp?.data?.data?.success) {
        const jobResp = await getJobDetailApi({
          params: {
            id: params?.jobId,
            jobType: payloadType?.inspection,
          },
        })
        if (jobResp?.data?.id) {
          const updatedData = getInitialFormData(jobResp?.data?.[keyName])
          const updatedTransferred = convertToFormData(
            jobResp?.data?.[keyName] || [],
          )
          if (length(filePath)) {
            inspectionForm.setFieldValue(filePath, updatedTransferred)
          }
          setRows(updatedData)
          setFormData(updatedData)
        }
      }
    } else {
      const updatedRows = rows.filter((_, i) => i !== index)
      const rowsWithUpdatedSNo = updatedRows.map((row, i) => ({
        ...row,
        sNo: i + 1,
      }))
      setRows(rowsWithUpdatedSNo)
      setFormData(rowsWithUpdatedSNo)
    }

    setDeleteConfirmation({
      open: false,
      record: null,
      index: null,
    })
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      open: false,
      record: null,
      index: null,
    })
  }

  const getViewData = useCallback(
    record => {
      if (!record) return []

      const viewFormAttrs = childMotherFormAttrFn({
        photoOfBeneficiaryUploadedValue:
          record?.photoOfBeneficiaryUploadedYesNo,
        takeHomeRationTHRValue: record?.takeHomeRation,
        severeAcuteMalnutritionSAMValue: record?.sam,
        moderateAcuteMalnutritionMAMValue: record?.mam,
      })

      const viewData = []
      entries(viewFormAttrs)?.forEach(([key, attr]) => {
        const value = record?.[key]
        let displayValue = '-'

        if (isEqual(attr?.inputType, 'formUpload')) {
          const fileList = value?.fileList || []
          if (length(fileList)) {
            const fileUrls = fileList
              .map(file => file?.thumbUrl || file?.url || file?.fileUrl)
              .filter(Boolean)
            displayValue = length(fileUrls)
              ? { type: 'images', urls: fileUrls }
              : '-'
          }
        } else if (isEqual(attr?.inputType, 'select')) {
          const optKeyMap = attr?.options?.reduce((acc, item) => {
            acc[item?.value] = item?.label
            return acc
          }, {})
          displayValue = optKeyMap?.[value]
            ? t(optKeyMap?.[value])
            : value || '-'
        } else if (isEqual(attr?.inputType, 'textArea')) {
          displayValue = value || '-'
        } else {
          displayValue = value || '-'
        }

        viewData.push({
          label: attr?.label,
          value: displayValue,
          inputType: attr?.inputType,
        })
      })

      return viewData
    },
    [childMotherFormAttrFn, t],
  )

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setEditingIndex(null)
    modalForm.resetFields()
  }

  const convertToFormData = updatedRows => {
    if (!updatedRows || !isArray(updatedRows)) {
      return []
    }
    const transformedRows = updatedRows.map(row => {
      const currentAttr = childMotherFormAttrFn({
        photoOfBeneficiaryUploadedValue: row?.photoOfBeneficiaryUploadedYesNo,
        takeHomeRationTHRValue: row?.takeHomeRation,
        severeAcuteMalnutritionSAMValue: row?.sam,
        moderateAcuteMalnutritionMAMValue: row?.mam,
      })
      entries(currentAttr)?.forEach(([key, attr]) => {
        if (isEqual(attr?.inputType, 'formUpload') && row?.[key]) {
          const fileList = row[key]?.fileList || []
          const dmsIds = row[key]?.fileList
            ? fileList
                ?.map(file => file?.dmsId)
                ?.filter(dmsId => dmsId !== null && dmsId !== undefined)
            : row[key]
          row[key] = length(dmsIds) ? dmsIds : []
        }
      })
      return row
    })
    return transformedRows
  }

  const handleModalFinish = async values => {
    const updatedRows = (() => {
      if (editingIndex !== null && editingIndex !== undefined) {
        const next = [...rows]
        next[editingIndex] = {
          ...values,
          ...(next[editingIndex]?.id ? { id: next[editingIndex]?.id } : {}),
        }
        return next
      }

      const nextIndex = rows.length + 1
      return [
        ...rows,
        {
          sNo: nextIndex,
          ...values,
        },
      ]
    })()

    setRows(updatedRows)
    const transformedRows = convertToFormData(updatedRows)
    if (length(filePath)) {
      inspectionForm.setFieldValue(filePath, transformedRows)
    }

    const apiResponse = apiCall
      ? await apiCall({
          index: filePath?.[1],
          isLoading: false,
          showMsg: false,
        })
      : null
    const updatedData = apiResponse
      ? getInitialFormData(apiResponse?.[keyName])
      : null
    const updatedTransferred = convertToFormData(apiResponse?.[keyName] || [])
    if (length(filePath)) {
      inspectionForm.setFieldValue(filePath, updatedTransferred)
    }
    setRows(updatedData)
    handleModalCancel()
  }

  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo?.current || prev.current,
      pageSize: paginationInfo?.pageSize || prev.pageSize,
      total: length(rows) || 0,
    }))
  }

  // Update pagination total when rows change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: length(rows) || 0,
    }))
  }, [rows])

  return {
    tableColumns,
    handleAddClick,
    isAddDisabled,
    rows,
    handleEditClick,
    handleViewClick,
    handleViewModalCancel,
    handleModalCancel,
    handleModalFinish,
    editingIndex,
    modalForm,
    formFieldAttributes,
    isModalOpen,
    isViewModalOpen,
    viewingRecord,
    getViewData,
    cardViewFn,
    handleTableChange,
    pagination,
    handleDeleteClick,
    deleteConfirmation,
    handleDeleteConfirm,
    handleDeleteCancel,
  }
}

export default childMotherTable
